/**
 * Shared message transformation utilities.
 * These utilities provide common functionality for message handling across frontend and backend.
 */

/**
 * Maps various role formats to standard roles.
 * Single source of truth for role mapping.
 */
export function mapMessageRole(role: string): 'system' | 'user' | 'assistant' {
  switch (role.toLowerCase()) {
    case 'system':
      return 'system';
    case 'assistant':
    case 'ai':
      return 'assistant';
    case 'user':
    case 'human':
    default:
      return 'user';
  }
}

/**
 * Validates if a role is valid.
 */
export function isValidRole(role: string): role is 'system' | 'user' | 'assistant' {
  return ['system', 'user', 'assistant'].includes(role.toLowerCase());
}

/**
 * Validates message content and structure.
 * Returns validation result with specific errors.
 */
export interface MessageValidationResult {
  isValid: boolean;
  errors: string[];
}

export function validateMessage(message: {
  role: string;
  content: string | null | undefined;
}): MessageValidationResult {
  const errors: string[] = [];

  // Validate role
  if (!isValidRole(message.role)) {
    errors.push(`Invalid message role: ${message.role}`);
  }

  // Validate content
  if (message.content === null || message.content === undefined) {
    errors.push('Message missing content');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Sanitizes message content by removing null/undefined and invalid roles.
 */
export function sanitizeMessages<T extends { role: string; content: string | null | undefined }>(
  messages: T[]
): T[] {
  return messages.filter(msg => {
    const validation = validateMessage(msg);
    if (!validation.isValid) {
      console.warn('Invalid message filtered out:', validation.errors.join(', '));
      return false;
    }
    return true;
  });
}

/**
 * Common content part types used across the application.
 * These provide a unified way to represent multimodal content.
 */
export interface TextContentPart {
  type: 'text';
  text: string;
}

export interface ImageContentPart {
  type: 'image';
  imageUrl: string;
  detail?: 'auto' | 'low' | 'high';
}

export interface FileContentPart {
  type: 'file';
  fileId: string;
  fileName: string;
  mimeType: string;
  content?: string;
}

export type ContentPart = TextContentPart | ImageContentPart | FileContentPart;

/**
 * Type guards for content parts
 */
export function isTextContent(part: ContentPart): part is TextContentPart {
  return part.type === 'text';
}

export function isImageContent(part: ContentPart): part is ImageContentPart {
  return part.type === 'image';
}

export function isFileContent(part: ContentPart): part is FileContentPart {
  return part.type === 'file';
}

/**
 * Creates a text content part
 */
export function createTextPart(text: string): TextContentPart {
  return { type: 'text', text };
}

/**
 * Creates an image content part
 */
export function createImagePart(
  imageUrl: string,
  detail?: 'auto' | 'low' | 'high'
): ImageContentPart {
  return { type: 'image', imageUrl, detail };
}

/**
 * Creates a file content part
 */
export function createFilePart(
  fileId: string,
  fileName: string,
  mimeType: string,
  content?: string
): FileContentPart {
  return { type: 'file', fileId, fileName, mimeType, content };
}